import { axiosSecuredInstance } from "configurations/axiosConfig";
import { PolicyNote } from "entities/UIModel/PolicyManagementRecord";
import { showToast } from "controls/global/stewart-toast";

// Mirrors Stewart.Shared.Entities.PolicyData.PolicyNote (list endpoint returns
// PaginatedPolicyNote, which extends it). PascalCase on the wire because
// Startup.cs sets JsonSerializerOptions.PropertyNamingPolicy = null.
export type PolicyNoteDto = {
  PolicyNoteID?: number | null;
  PolicyHistoryID?: number | null;
  PolicyTransactionID?: number | null;
  PolicyNumber?: string | null;
  AgentID?: string | null;
  StateCode?: string | null;
  NoteEnteredUserID?: string | null;
  NoteEnteredUserName?: string | null;
  PolicyNoteTypeCode?: string | null;
  PolicyNoteTypeName?: string | null; // read-only, from PolicyNoteType join
  SubType?: string | null;
  NoteText?: string | null;
  NoteDate?: string | null; // DateTimeOffset
  FromStage?: string | null;
  ToStage?: string | null;
  PolicyActivityID?: number | null;
};

// The [PolicyNote_GetListByTransaction] proc consumes PolicyTransactionID (+
// optional user/paging fields); other criteria props are ignored by that proc.
export type PolicyNoteCriteriaDto = {
  PolicyTransactionID: number;
  UserTimeZone: string;
  SortColumn?: string;
  ListCount?: number;
  RequestedPage?: number;
};

export const mapNoteDtoToPolicyNote = (dto: PolicyNoteDto): PolicyNote => {
  const text = dto.NoteText ?? "";
  return {
    id: dto.PolicyNoteID ?? undefined,
    type: dto.PolicyNoteTypeName ?? dto.PolicyNoteTypeCode ?? "",
    text,
    description: text,
    by: dto.NoteEnteredUserName ?? "",
    at: dto.NoteDate ?? "",
  };
};

export const getPolicyNoteListByTransaction = async (
  criteria: PolicyNoteCriteriaDto
): Promise<PolicyNoteDto[]> => {
  try {
    const { data } = await axiosSecuredInstance.post<PolicyNoteDto[]>(
      "/PolicyNote/GetPolicyNoteListByTransaction",
      criteria
    );
    return Array.isArray(data) ? data : [];
  } catch {
    showToast({ type: "error", message: "Failed to load notes." });
    return [];
  }
};

// Adds or updates a note via the single PolicyNote_UpsertSingle proc
// (SavePolicyNote endpoint): no PolicyNoteID => INSERT, PolicyNoteID > 0 =>
// UPDATE NoteText by PolicyNoteID. Returns true on any 2xx.
export const savePolicyNote = async (
  entity: PolicyNoteDto
): Promise<boolean> => {
  try {
    await axiosSecuredInstance.post(
      "/PolicyNote/SavePolicyNote",
      entity
    );
    return true;
  } catch {
    showToast({ type: "error", message: "Failed to save note." });
    return false;
  }
};
